import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Eye, User, Image, 
  MessageCircle, Check, X, ExternalLink, Globe,
  FileText, Users, Megaphone, List, ListOrdered, Quote, Link2
} from 'lucide-react';

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapUnderline from '@tiptap/extension-underline';
import TiptapLink from '@tiptap/extension-link';

// Rich Text Editor using Tiptap - a proper WYSIWYG editor
const RichTextEditor = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
      }),
      TiptapUnderline,
      TiptapLink.configure({
        openOnClick: false,
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Update content when value prop changes externally
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  if (!editor) {
    return <div className="min-h-[300px] border rounded-lg p-4 animate-pulse bg-gray-100" />;
  }

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div className="border-2 rounded-lg overflow-hidden">
      {/* Sticky Toolbar */}
      <div className="rich-text-toolbar flex flex-wrap gap-1 p-3 border-b-2 bg-gray-100 dark:bg-gray-800 sticky top-0 z-20">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`font-bold border-2 ${editor.isActive('bold') ? 'bg-gray-300 dark:bg-gray-600' : ''}`}
          data-testid="toolbar-bold"
        >
          B
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`italic border-2 ${editor.isActive('italic') ? 'bg-gray-300 dark:bg-gray-600' : ''}`}
          data-testid="toolbar-italic"
        >
          I
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`underline border-2 ${editor.isActive('underline') ? 'bg-gray-300 dark:bg-gray-600' : ''}`}
          data-testid="toolbar-underline"
        >
          U
        </Button>
        
        <div className="w-px bg-gray-400 mx-1 self-stretch" />
        
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`font-bold border-2 ${editor.isActive('heading', { level: 2 }) ? 'bg-gray-300 dark:bg-gray-600' : ''}`}
          data-testid="toolbar-h2"
        >
          H2
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`font-bold border-2 ${editor.isActive('heading', { level: 3 }) ? 'bg-gray-300 dark:bg-gray-600' : ''}`}
          data-testid="toolbar-h3"
        >
          H3
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => editor.chain().focus().setParagraph().run()}
          className={`font-bold border-2 ${editor.isActive('paragraph') ? 'bg-gray-300 dark:bg-gray-600' : ''}`}
          data-testid="toolbar-p"
        >
          P
        </Button>
        
        <div className="w-px bg-gray-400 mx-1 self-stretch" />
        
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`border-2 ${editor.isActive('bulletList') ? 'bg-gray-300 dark:bg-gray-600' : ''}`}
          data-testid="toolbar-bullet-list"
        >
          <List className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`border-2 ${editor.isActive('orderedList') ? 'bg-gray-300 dark:bg-gray-600' : ''}`}
          data-testid="toolbar-ordered-list"
        >
          <ListOrdered className="w-4 h-4" />
        </Button>
        
        <div className="w-px bg-gray-400 mx-1 self-stretch" />
        
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addLink}
          className={`border-2 ${editor.isActive('link') ? 'bg-gray-300 dark:bg-gray-600' : ''}`}
          data-testid="toolbar-link"
        >
          <Link2 className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={`border-2 ${editor.isActive('blockquote') ? 'bg-gray-300 dark:bg-gray-600' : ''}`}
          data-testid="toolbar-quote"
        >
          <Quote className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Editor Content */}
      <EditorContent 
        editor={editor} 
        className="prose prose-sm max-w-none min-h-[300px] p-4 focus:outline-none"
        data-testid="rich-text-editor"
      />
    </div>
  );
};

const BlogAdminPanel = ({ adminToken }) => {
  // Posts state
  const [posts, setPosts] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [comments, setComments] = useState([]);
  const [ads, setAds] = useState([]);
  const [seoSettings, setSeoSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Dialog states
  const [postDialog, setPostDialog] = useState(false);
  const [authorDialog, setAuthorDialog] = useState(false);
  const [adDialog, setAdDialog] = useState(false);
  const [seoDialog, setSeoDialog] = useState(false);
  
  // Form states
  const [currentPost, setCurrentPost] = useState(null);
  const [currentAuthor, setCurrentAuthor] = useState(null);
  const [currentAd, setCurrentAd] = useState(null);
  
  const [postForm, setPostForm] = useState({
    title: '', subtitle: '', content: '', excerpt: '', featured_image: '',
    author_id: '', tags: '', status: 'draft', scheduled_at: '', seo_title: '', seo_description: '', seo_keywords: ''
  });
  
  const [authorForm, setAuthorForm] = useState({
    name: '', affiliation: '', bio: '', photo_url: '',
    social_twitter: '', social_instagram: '', social_linkedin: '', social_website: ''
  });
  
  const [adForm, setAdForm] = useState({
    name: '', image_url: '', link_url: '', position: 'sidebar', active: true
  });
  
  const [seoForm, setSeoForm] = useState({
    site_title: '', site_description: '', default_keywords: '', competitor_urls: '', google_analytics_id: ''
  });
  
  const [commentFilter, setCommentFilter] = useState('pending');
  const [uploadingImage, setUploadingImage] = useState(false);

  const headers = { 'X-Admin-Token': adminToken };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [postsRes, authorsRes, commentsRes, adsRes, seoRes] = await Promise.all([
        axios.get(`${API}/admin/blog/posts`, { headers }),
        axios.get(`${API}/authors`),
        axios.get(`${API}/admin/blog/comments?status=${commentFilter}`, { headers }),
        axios.get(`${API}/admin/blog/ads`, { headers }),
        axios.get(`${API}/admin/seo`, { headers })
      ]);
      setPosts(postsRes.data);
      setAuthors(authorsRes.data);
      setComments(commentsRes.data);
      setAds(adsRes.data);
      setSeoSettings(seoRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async (filter = commentFilter) => {
    try {
      const response = await axios.get(`${API}/admin/blog/comments?status=${filter}`, { headers });
      setComments(response.data);
    } catch (error) {
      toast.error('Failed to load comments');
    }
  };

  // Image upload handler
  const handleImageUpload = async (file, callback) => {
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/admin/blog/upload-image`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      callback(response.data.url);
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  // Post CRUD
  const openPostDialog = (post = null) => {
    if (post) {
      setCurrentPost(post);
      setPostForm({
        title: post.title || '',
        subtitle: post.subtitle || '',
        content: post.content || '',
        excerpt: post.excerpt || '',
        featured_image: post.featured_image || '',
        author_id: post.author_id || '',
        tags: (post.tags || []).join(', '),
        status: post.status || 'draft',
        scheduled_at: post.scheduled_at ? new Date(post.scheduled_at).toISOString().slice(0, 16) : '',
        seo_title: post.seo_title || '',
        seo_description: post.seo_description || '',
        seo_keywords: (post.seo_keywords || []).join(', ')
      });
    } else {
      setCurrentPost(null);
      setPostForm({
        title: '', subtitle: '', content: '', excerpt: '', featured_image: '',
        author_id: authors[0]?.author_id || '', tags: '', status: 'draft',
        scheduled_at: '', seo_title: '', seo_description: '', seo_keywords: ''
      });
    }
    setPostDialog(true);
  };

  const savePost = async () => {
    if (!postForm.title || !postForm.content || !postForm.author_id) {
      toast.error('Title, content, and author are required');
      return;
    }

    // Validate scheduled posts have a date
    if (postForm.status === 'scheduled' && !postForm.scheduled_at) {
      toast.error('Please set a publish date for scheduled posts');
      return;
    }

    try {
      const payload = {
        ...postForm,
        tags: postForm.tags.split(',').map(t => t.trim()).filter(Boolean),
        seo_keywords: postForm.seo_keywords.split(',').map(k => k.trim()).filter(Boolean),
        scheduled_at: postForm.status === 'scheduled' && postForm.scheduled_at 
          ? new Date(postForm.scheduled_at).toISOString() 
          : null
      };

      if (currentPost) {
        await axios.put(`${API}/api/admin/blog/posts/${currentPost.post_id}`, payload, { headers });
        toast.success('Post updated');
      } else {
        await axios.post(`${API}/admin/blog/posts`, payload, { headers });
        toast.success('Post created');
      }
      setPostDialog(false);
      loadData();
    } catch (error) {
      toast.error('Failed to save post');
    }
  };

  const deletePost = async (postId) => {
    if (!confirm('Delete this post? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api/admin/blog/posts/${postId}`, { headers });
      toast.success('Post deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  // Author CRUD
  const openAuthorDialog = (author = null) => {
    if (author) {
      setCurrentAuthor(author);
      setAuthorForm({
        name: author.name || '',
        affiliation: author.affiliation || '',
        bio: author.bio || '',
        photo_url: author.photo_url || '',
        social_twitter: author.social_links?.twitter || '',
        social_instagram: author.social_links?.instagram || '',
        social_linkedin: author.social_links?.linkedin || '',
        social_website: author.social_links?.website || ''
      });
    } else {
      setCurrentAuthor(null);
      setAuthorForm({
        name: '', affiliation: '', bio: '', photo_url: '',
        social_twitter: '', social_instagram: '', social_linkedin: '', social_website: ''
      });
    }
    setAuthorDialog(true);
  };

  const saveAuthor = async () => {
    if (!authorForm.name) {
      toast.error('Name is required');
      return;
    }

    try {
      const payload = {
        name: authorForm.name,
        affiliation: authorForm.affiliation,
        bio: authorForm.bio,
        photo_url: authorForm.photo_url,
        social_links: {
          twitter: authorForm.social_twitter,
          instagram: authorForm.social_instagram,
          linkedin: authorForm.social_linkedin,
          website: authorForm.social_website
        }
      };

      if (currentAuthor) {
        await axios.put(`${API}/admin/authors/${currentAuthor.author_id}`, payload, { headers });
        toast.success('Author updated');
      } else {
        await axios.post(`${API}/admin/authors`, payload, { headers });
        toast.success('Author created');
      }
      setAuthorDialog(false);
      loadData();
    } catch (error) {
      toast.error('Failed to save author');
    }
  };

  const deleteAuthor = async (authorId) => {
    if (!confirm('Delete this author?')) return;
    try {
      await axios.delete(`${API}/admin/authors/${authorId}`, { headers });
      toast.success('Author deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete author');
    }
  };

  // Comment moderation
  const updateComment = async (commentId, status) => {
    try {
      await axios.put(`${API}/admin/blog/comments/${commentId}`, { status }, { headers });
      toast.success(`Comment ${status}`);
      loadComments();
    } catch (error) {
      toast.error('Failed to update comment');
    }
  };

  const deleteComment = async (commentId) => {
    if (!confirm('Delete this comment?')) return;
    try {
      await axios.delete(`${API}/admin/blog/comments/${commentId}`, { headers });
      toast.success('Comment deleted');
      loadComments();
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  // Ad CRUD
  const openAdDialog = (ad = null) => {
    if (ad) {
      setCurrentAd(ad);
      setAdForm({
        name: ad.name || '',
        image_url: ad.image_url || '',
        link_url: ad.link_url || '',
        position: ad.position || 'sidebar',
        active: ad.active !== false
      });
    } else {
      setCurrentAd(null);
      setAdForm({ name: '', image_url: '', link_url: '', position: 'sidebar', active: true });
    }
    setAdDialog(true);
  };

  const saveAd = async () => {
    if (!adForm.name || !adForm.image_url || !adForm.link_url) {
      toast.error('All fields are required');
      return;
    }

    try {
      if (currentAd) {
        await axios.put(`${API}/api/admin/blog/ads/${currentAd.ad_id}`, adForm, { headers });
        toast.success('Ad updated');
      } else {
        await axios.post(`${API}/admin/blog/ads`, adForm, { headers });
        toast.success('Ad created');
      }
      setAdDialog(false);
      loadData();
    } catch (error) {
      toast.error('Failed to save ad');
    }
  };

  const deleteAd = async (adId) => {
    if (!confirm('Delete this ad?')) return;
    try {
      await axios.delete(`${API}/api/admin/blog/ads/${adId}`, { headers });
      toast.success('Ad deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete ad');
    }
  };

  // SEO
  const openSeoDialog = () => {
    if (seoSettings) {
      setSeoForm({
        site_title: seoSettings.site_title || '',
        site_description: seoSettings.site_description || '',
        default_keywords: (seoSettings.default_keywords || []).join(', '),
        competitor_urls: (seoSettings.competitor_urls || []).join('\n'),
        google_analytics_id: seoSettings.google_analytics_id || ''
      });
    }
    setSeoDialog(true);
  };

  const saveSeo = async () => {
    try {
      const payload = {
        site_title: seoForm.site_title,
        site_description: seoForm.site_description,
        default_keywords: seoForm.default_keywords.split(',').map(k => k.trim()).filter(Boolean),
        competitor_urls: seoForm.competitor_urls.split('\n').map(u => u.trim()).filter(Boolean),
        google_analytics_id: seoForm.google_analytics_id
      };
      await axios.put(`${API}/admin/seo`, payload, { headers });
      toast.success('SEO settings saved');
      setSeoDialog(false);
      loadData();
    } catch (error) {
      toast.error('Failed to save SEO settings');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const pendingComments = comments.filter(c => c.status === 'pending').length;

  return (
    <div className="space-y-6">
      <Tabs defaultValue="posts" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="posts" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Posts ({posts.length})
          </TabsTrigger>
          <TabsTrigger value="authors" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Authors ({authors.length})
          </TabsTrigger>
          <TabsTrigger value="comments" className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4" />
            Comments
            {pendingComments > 0 && (
              <Badge variant="destructive" className="ml-1">{pendingComments}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="ads" className="flex items-center gap-2">
            <Megaphone className="w-4 h-4" />
            Ads ({ads.length})
          </TabsTrigger>
          <TabsTrigger value="seo" className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            SEO
          </TabsTrigger>
        </TabsList>

        {/* Posts Tab */}
        <TabsContent value="posts" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-muted-foreground">Manage blog posts</p>
            <Button onClick={() => openPostDialog()} data-testid="add-post-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Post
            </Button>
          </div>

          <div className="space-y-3">
            {posts.length === 0 ? (
              <Card className="p-8 text-center text-muted-foreground">
                No posts yet. Create your first blog post!
              </Card>
            ) : (
              posts.map(post => (
                <Card key={post.post_id} className="p-4">
                  <div className="flex items-start gap-4">
                    {post.featured_image && (
                      <img src={post.featured_image} alt="" className="w-24 h-16 object-cover rounded" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold truncate">{post.title}</h3>
                        <Badge variant={
                          post.status === 'published' ? 'default' : 
                          post.status === 'scheduled' ? 'outline' : 'secondary'
                        }>
                          {post.status === 'scheduled' 
                            ? `Scheduled: ${formatDate(post.scheduled_at)}`
                            : post.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        By {post.author_name} • {formatDate(post.created_at)}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{post.views_count}</span>
                        <span className="flex items-center gap-1">❤️ {post.likes_count}</span>
                        <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />{post.comments_count}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {post.status === 'published' && (
                        <Button variant="ghost" size="sm" onClick={() => window.open(`/blog/${post.slug}`, '_blank')}>
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => openPostDialog(post)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deletePost(post.post_id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Authors Tab */}
        <TabsContent value="authors" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-muted-foreground">Manage blog authors</p>
            <Button onClick={() => openAuthorDialog()} data-testid="add-author-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Author
            </Button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {authors.map(author => (
              <Card key={author.author_id} className="p-4">
                <div className="flex items-start gap-3">
                  {author.photo_url ? (
                    <img src={author.photo_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                      <User className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate">{author.name}</h3>
                    {author.affiliation && (
                      <p className="text-sm text-muted-foreground truncate">{author.affiliation}</p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openAuthorDialog(author)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteAuthor(author.author_id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Comments Tab */}
        <TabsContent value="comments" className="space-y-4">
          <div className="flex justify-between items-center">
            <Select value={commentFilter} onValueChange={(v) => { setCommentFilter(v); loadComments(v); }}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            {comments.length === 0 ? (
              <Card className="p-8 text-center text-muted-foreground">
                No {commentFilter} comments
              </Card>
            ) : (
              comments.map(comment => (
                <Card key={comment.comment_id} className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{comment.author_name}</span>
                        <span className="text-xs text-muted-foreground">on &ldquo;{comment.post_title}&rdquo;</span>
                      </div>
                      <p className="text-sm mb-2">{comment.content}</p>
                      <span className="text-xs text-muted-foreground">{formatDate(comment.created_at)}</span>
                    </div>
                    <div className="flex gap-2">
                      {comment.status === 'pending' && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => updateComment(comment.comment_id, 'approved')}>
                            <Check className="w-4 h-4 text-green-500" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => updateComment(comment.comment_id, 'rejected')}>
                            <X className="w-4 h-4 text-red-500" />
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => deleteComment(comment.comment_id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Ads Tab */}
        <TabsContent value="ads" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-muted-foreground">Manage affiliate ads (image + link)</p>
            <Button onClick={() => openAdDialog()} data-testid="add-ad-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Ad
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {ads.map(ad => (
              <Card key={ad.ad_id} className="p-4">
                <div className="flex items-start gap-4">
                  <img src={ad.image_url} alt="" className="w-24 h-16 object-cover rounded" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold truncate">{ad.name}</h3>
                      <Badge variant={ad.active ? 'default' : 'secondary'}>
                        {ad.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">Position: {ad.position}</p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>{ad.impression_count} impressions</span>
                      <span>{ad.click_count} clicks</span>
                      <span>{ad.impression_count > 0 ? ((ad.click_count / ad.impression_count) * 100).toFixed(1) : 0}% CTR</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openAdDialog(ad)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteAd(ad.ad_id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* SEO Tab */}
        <TabsContent value="seo" className="space-y-4">
          <Card className="p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-lg font-semibold">SEO Settings</h3>
                <p className="text-sm text-muted-foreground">Configure site-wide SEO settings</p>
              </div>
              <Button onClick={openSeoDialog}>
                <Edit className="w-4 h-4 mr-2" />
                Edit Settings
              </Button>
            </div>

            {seoSettings && (
              <div className="space-y-4">
                <div>
                  <Label className="text-muted-foreground">Site Title</Label>
                  <p className="font-medium">{seoSettings.site_title}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Site Description</Label>
                  <p className="font-medium">{seoSettings.site_description}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Default Keywords</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(seoSettings.default_keywords || []).map((kw, i) => (
                      <Badge key={i} variant="secondary">{kw}</Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Competitor URLs to Track</Label>
                  <div className="space-y-1 mt-1">
                    {(seoSettings.competitor_urls || []).map((url, i) => (
                      <p key={i} className="text-sm">{url}</p>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Sitemap</Label>
                  <p className="text-sm">
                    <a href="/api/sitemap.xml" target="_blank" className="text-blue-600 hover:underline">
                      View Sitemap.xml
                    </a>
                  </p>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Post Dialog */}
      <Dialog open={postDialog} onOpenChange={setPostDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{currentPost ? 'Edit Post' : 'New Blog Post'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Title *</Label>
                <Input
                  value={postForm.title}
                  onChange={(e) => setPostForm({ ...postForm, title: e.target.value })}
                  placeholder="Post title"
                />
              </div>
              <div className="col-span-2">
                <Label>Subtitle</Label>
                <Input
                  value={postForm.subtitle}
                  onChange={(e) => setPostForm({ ...postForm, subtitle: e.target.value })}
                  placeholder="Optional subtitle"
                />
              </div>
              <div>
                <Label>Author *</Label>
                <Select value={postForm.author_id} onValueChange={(v) => setPostForm({ ...postForm, author_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select author" />
                  </SelectTrigger>
                  <SelectContent>
                    {authors.map(a => (
                      <SelectItem key={a.author_id} value={a.author_id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={postForm.status} onValueChange={(v) => setPostForm({ ...postForm, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {postForm.status === 'scheduled' && (
                <div className="col-span-2">
                  <Label>Publish Date & Time *</Label>
                  <Input
                    type="datetime-local"
                    value={postForm.scheduled_at}
                    onChange={(e) => setPostForm({ ...postForm, scheduled_at: e.target.value })}
                    min={new Date().toISOString().slice(0, 16)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Post will be automatically published at this time (UTC)
                  </p>
                </div>
              )}
              <div className="col-span-2">
                <Label>Featured Image</Label>
                <div className="flex gap-2">
                  <Input
                    value={postForm.featured_image}
                    onChange={(e) => setPostForm({ ...postForm, featured_image: e.target.value })}
                    placeholder="Image URL"
                  />
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    id="featured-image-upload"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handleImageUpload(e.target.files[0], (url) => setPostForm({ ...postForm, featured_image: url }));
                      }
                    }}
                  />
                  <Button type="button" variant="outline" onClick={() => document.getElementById('featured-image-upload').click()}>
                    <Image className="w-4 h-4 mr-2" />
                    Upload
                  </Button>
                </div>
              </div>
              <div className="col-span-2">
                <Label>Tags (comma-separated)</Label>
                <Input
                  value={postForm.tags}
                  onChange={(e) => setPostForm({ ...postForm, tags: e.target.value })}
                  placeholder="faith, prayer, devotion"
                />
              </div>
              <div className="col-span-2">
                <Label>Content *</Label>
                <RichTextEditor
                  value={postForm.content}
                  onChange={(html) => setPostForm({ ...postForm, content: html })}
                />
              </div>
              <div className="col-span-2">
                <Label>Excerpt (auto-generated if empty)</Label>
                <Textarea
                  value={postForm.excerpt}
                  onChange={(e) => setPostForm({ ...postForm, excerpt: e.target.value })}
                  rows={2}
                  placeholder="Short preview text"
                />
              </div>
              <div className="col-span-2 border-t pt-4">
                <h4 className="font-medium mb-2">SEO Settings</h4>
              </div>
              <div className="col-span-2">
                <Label>SEO Title (defaults to post title)</Label>
                <Input
                  value={postForm.seo_title}
                  onChange={(e) => setPostForm({ ...postForm, seo_title: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label>SEO Description</Label>
                <Textarea
                  value={postForm.seo_description}
                  onChange={(e) => setPostForm({ ...postForm, seo_description: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="col-span-2">
                <Label>SEO Keywords (comma-separated)</Label>
                <Input
                  value={postForm.seo_keywords}
                  onChange={(e) => setPostForm({ ...postForm, seo_keywords: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPostDialog(false)}>Cancel</Button>
            <Button onClick={savePost}>{currentPost ? 'Update' : 'Create'} Post</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Author Dialog */}
      <Dialog open={authorDialog} onOpenChange={setAuthorDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{currentAuthor ? 'Edit Author' : 'New Author'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={authorForm.name}
                onChange={(e) => setAuthorForm({ ...authorForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Affiliation</Label>
              <Input
                value={authorForm.affiliation}
                onChange={(e) => setAuthorForm({ ...authorForm, affiliation: e.target.value })}
                placeholder="Church, Organization, etc."
              />
            </div>
            <div>
              <Label>Bio</Label>
              <Textarea
                value={authorForm.bio}
                onChange={(e) => setAuthorForm({ ...authorForm, bio: e.target.value })}
                rows={3}
              />
            </div>
            <div>
              <Label>Profile Photo</Label>
              <div className="space-y-3">
                {/* Photo Preview */}
                {authorForm.photo_url && (
                  <div className="flex items-center gap-3">
                    <img 
                      src={authorForm.photo_url} 
                      alt="Author preview" 
                      className="w-16 h-16 rounded-full object-cover border"
                    />
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setAuthorForm({ ...authorForm, photo_url: '' })}
                      className="text-red-500 hover:text-red-600"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                )}
                
                {/* Upload Section */}
                <div className="flex gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    id="author-photo-upload"
                    data-testid="author-photo-file-input"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handleImageUpload(e.target.files[0], (url) => setAuthorForm({ ...authorForm, photo_url: url }));
                      }
                    }}
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => document.getElementById('author-photo-upload').click()}
                    disabled={uploadingImage}
                    data-testid="author-photo-upload-btn"
                    className="flex-1"
                  >
                    {uploadingImage ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Image className="w-4 h-4 mr-2" />
                        Upload Photo from Device
                      </>
                    )}
                  </Button>
                </div>
                
                {/* URL Input (Alternative) */}
                <div>
                  <Label className="text-xs text-muted-foreground">Or paste image URL:</Label>
                  <Input
                    value={authorForm.photo_url}
                    onChange={(e) => setAuthorForm({ ...authorForm, photo_url: e.target.value })}
                    placeholder="https://example.com/photo.jpg"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Twitter URL</Label>
                <Input
                  value={authorForm.social_twitter}
                  onChange={(e) => setAuthorForm({ ...authorForm, social_twitter: e.target.value })}
                />
              </div>
              <div>
                <Label>Instagram URL</Label>
                <Input
                  value={authorForm.social_instagram}
                  onChange={(e) => setAuthorForm({ ...authorForm, social_instagram: e.target.value })}
                />
              </div>
              <div>
                <Label>LinkedIn URL</Label>
                <Input
                  value={authorForm.social_linkedin}
                  onChange={(e) => setAuthorForm({ ...authorForm, social_linkedin: e.target.value })}
                />
              </div>
              <div>
                <Label>Website URL</Label>
                <Input
                  value={authorForm.social_website}
                  onChange={(e) => setAuthorForm({ ...authorForm, social_website: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAuthorDialog(false)}>Cancel</Button>
            <Button onClick={saveAuthor}>{currentAuthor ? 'Update' : 'Create'} Author</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ad Dialog */}
      <Dialog open={adDialog} onOpenChange={setAdDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{currentAd ? 'Edit Ad' : 'New Ad'}</DialogTitle>
            <DialogDescription>
              Create simple image ads with affiliate links
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Ad Name *</Label>
              <Input
                value={adForm.name}
                onChange={(e) => setAdForm({ ...adForm, name: e.target.value })}
                placeholder="Affiliate Partner Banner"
              />
            </div>
            <div>
              <Label>Image URL *</Label>
              <div className="flex gap-2">
                <Input
                  value={adForm.image_url}
                  onChange={(e) => setAdForm({ ...adForm, image_url: e.target.value })}
                />
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  id="ad-image-upload"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleImageUpload(e.target.files[0], (url) => setAdForm({ ...adForm, image_url: url }));
                    }
                  }}
                />
                <Button type="button" variant="outline" onClick={() => document.getElementById('ad-image-upload').click()}>
                  Upload
                </Button>
              </div>
            </div>
            <div>
              <Label>Link URL * (affiliate link)</Label>
              <Input
                value={adForm.link_url}
                onChange={(e) => setAdForm({ ...adForm, link_url: e.target.value })}
                placeholder="https://affiliate-link.com/..."
              />
            </div>
            <div>
              <Label>Position</Label>
              <Select value={adForm.position} onValueChange={(v) => setAdForm({ ...adForm, position: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sidebar">Sidebar</SelectItem>
                  <SelectItem value="bottom">Bottom of Post</SelectItem>
                  <SelectItem value="in-content">In Content</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={adForm.active}
                onCheckedChange={(v) => setAdForm({ ...adForm, active: v })}
              />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdDialog(false)}>Cancel</Button>
            <Button onClick={saveAd}>{currentAd ? 'Update' : 'Create'} Ad</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SEO Dialog */}
      <Dialog open={seoDialog} onOpenChange={setSeoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>SEO Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Site Title</Label>
              <Input
                value={seoForm.site_title}
                onChange={(e) => setSeoForm({ ...seoForm, site_title: e.target.value })}
              />
            </div>
            <div>
              <Label>Site Description</Label>
              <Textarea
                value={seoForm.site_description}
                onChange={(e) => setSeoForm({ ...seoForm, site_description: e.target.value })}
                rows={2}
              />
            </div>
            <div>
              <Label>Default Keywords (comma-separated)</Label>
              <Input
                value={seoForm.default_keywords}
                onChange={(e) => setSeoForm({ ...seoForm, default_keywords: e.target.value })}
                placeholder="devotional, faith, prayer, christian"
              />
            </div>
            <div>
              <Label>Competitor URLs (one per line)</Label>
              <Textarea
                value={seoForm.competitor_urls}
                onChange={(e) => setSeoForm({ ...seoForm, competitor_urls: e.target.value })}
                rows={3}
                placeholder="https://competitor1.com&#10;https://competitor2.com"
              />
            </div>
            <div>
              <Label>Google Analytics ID (optional)</Label>
              <Input
                value={seoForm.google_analytics_id}
                onChange={(e) => setSeoForm({ ...seoForm, google_analytics_id: e.target.value })}
                placeholder="G-XXXXXXXXXX"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSeoDialog(false)}>Cancel</Button>
            <Button onClick={saveSeo}>Save Settings</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogAdminPanel;
